import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { searchZones, zoneById, type ZoneInfo } from "../tz";
import { zoneAbbrs } from "../tz/aliases";

interface Props {
  value: string;
  onChange: (id: string) => void;
}

export function ZonePicker({ value, onChange }: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => searchZones(query), [query]);
  const info = zoneById.get(value);
  const label = info ? `${info.city} (${info.id})` : value;

  useEffect(() => setActive(0), [query]);
  // Close when clicking/tapping anywhere outside (the map swallows focus changes).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
        inputRef.current?.blur();
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);
  useEffect(() => {
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const choose = (z: ZoneInfo) => {
    onChange(z.id);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKey = (e: KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) choose(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      inputRef.current?.blur();
    }
  };

  return (
    <div
      class="zone-picker"
      ref={rootRef}
      onFocusOut={(e) => {
        const next = e.relatedTarget as Node | null;
        if (!next || !(e.currentTarget as HTMLElement).contains(next)) {
          setOpen(false);
          setQuery("");
        }
      }}
    >
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-label="Time zone"
        placeholder="Search city, country or zone…"
        value={open ? query : label}
        onInput={(e) => {
          setQuery((e.target as HTMLInputElement).value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      {open && (
        <ul class="zone-picker-list" ref={listRef} role="listbox">
          {results.length === 0 && <li class="muted">No matching zones</li>}
          {results.map((z, i) => (
            <li
              key={z.id}
              role="option"
              aria-selected={i === active}
              class={i === active ? "active" : ""}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(z);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span class="zone-city">{z.city}</span>
              <span class="zone-id">{z.id}</span>
              <span class="zone-meta">
                {z.countries.slice(0, 3).join(", ")}
                {z.comment ? ` · ${z.comment}` : ""}
                {zoneAbbrs.has(z.id) ? ` · ${zoneAbbrs.get(z.id)!.join("/")}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
